import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { tenantAPI, logAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

const SystemPage = () => {
  const [activeTab, setActiveTab] = useState('tenant');
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const queryClient = useQueryClient();

  // 获取租户信息
  const { data: tenantData, isLoading: tenantLoading } = useQuery(
    'tenantInfo',
    () => tenantAPI.getInfo()
  );

  // 获取用户列表
  const { data: usersData, isLoading: usersLoading } = useQuery(
    'tenantUsers',
    () => tenantAPI.getUsers()
  );

  // 更新租户信息
  const updateTenantMutation = useMutation(
    (data) => tenantAPI.update(data),
    {
      onSuccess: () => {
        toast.success('租户信息更新成功');
        setShowEditTenantModal(false);
        queryClient.invalidateQueries('tenantInfo');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '更新失败');
      }
    }
  );

  // 更新用户信息
  const updateUserMutation = useMutation(
    ({ id, data }) => tenantAPI.updateUser(id, data),
    {
      onSuccess: () => {
        toast.success('用户信息更新成功');
        setShowEditUserModal(false);
        setSelectedUser(null);
        queryClient.invalidateQueries('tenantUsers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '更新失败');
      }
    }
  );

  // 删除用户
  const deleteUserMutation = useMutation(
    (id) => tenantAPI.deleteUser(id),
    {
      onSuccess: () => {
        toast.success('用户已禁用');
        queryClient.invalidateQueries('tenantUsers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '删除失败');
      }
    }
  );

  // 创建用户
  const createUserMutation = useMutation(
    (data) => tenantAPI.createUser(data),
    {
      onSuccess: () => {
        toast.success('用户创建成功');
        setShowCreateUserModal(false);
        queryClient.invalidateQueries('tenantUsers');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '创建失败');
      }
    }
  );

  const tenant = tenantData?.tenant;
  const users = usersData?.users || [];

  const handleEditTenant = (data) => {
    updateTenantMutation.mutate(data);
  };

  const handleEditUser = (id, data) => {
    updateUserMutation.mutate({ id, data });
  };

  const handleDeleteUser = (id) => {
    if (window.confirm('确定要禁用这个用户吗？')) {
      deleteUserMutation.mutate(id);
    }
  };

  const handleCreateUser = (data) => {
    createUserMutation.mutate(data);
  };

  const tabs = [
    { id: 'tenant', name: '租户设置', icon: 'fa-building' },
    { id: 'users', name: '用户管理', icon: 'fa-users' },
    { id: 'roles', name: '角色权限', icon: 'fa-shield' },
    { id: 'logs', name: '系统日志', icon: 'fa-history' },
  ];

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">系统管理</h1>
        <p className="text-gray-600 mt-1">管理租户设置、用户权限和系统配置</p>
      </div>

      {/* 选项卡导航 */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center px-6 py-4 text-sm font-medium border-b-2
                  ${activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <i className={`fa ${tab.icon} mr-2`}></i>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* 选项卡内容 */}
        <div className="p-6">
          {tenantLoading && activeTab === 'tenant' ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="spinner mx-auto"></div>
                <p className="mt-4 text-gray-600">加载租户信息中...</p>
              </div>
            </div>
          ) : usersLoading && activeTab === 'users' ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="spinner mx-auto"></div>
                <p className="mt-4 text-gray-600">加载用户列表中...</p>
              </div>
            </div>
          ) : activeTab === 'tenant' ? (
            <TenantSettings
              tenant={tenant}
              onEdit={() => setShowEditTenantModal(true)}
            />
          ) : activeTab === 'users' ? (
            <UserManagement
              users={users}
              onEdit={(user) => {
                setSelectedUser(user);
                setShowEditUserModal(true);
              }}
              onDelete={handleDeleteUser}
              onCreate={() => setShowCreateUserModal(true)}
            />
          ) : activeTab === 'roles' ? (
            <RolePermissions />
          ) : activeTab === 'logs' ? (
            <SystemLogs />
          ) : null}
        </div>
      </div>

      {/* 编辑租户模态框 */}
      {showEditTenantModal && tenant && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <button
                onClick={() => setShowEditTenantModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">编辑租户信息</h3>
              </div>
              <div className="px-6 py-4">
                <TenantForm
                  tenant={tenant}
                  onSubmit={handleEditTenant}
                  onCancel={() => setShowEditTenantModal(false)}
                  isLoading={updateTenantMutation.isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 编辑用户模态框 */}
      {showEditUserModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setSelectedUser(null);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">编辑用户信息</h3>
              </div>
              <div className="px-6 py-4">
                <UserForm
                  user={selectedUser}
                  onSubmit={(data) => handleEditUser(selectedUser.id, data)}
                  onCancel={() => {
                    setShowEditUserModal(false);
                    setSelectedUser(null);
                  }}
                  isLoading={updateUserMutation.isLoading}
                  mode="edit"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 创建用户模态框 */}
      {showCreateUserModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
              <button
                onClick={() => setShowCreateUserModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">创建新用户</h3>
              </div>
              <div className="px-6 py-4">
                <UserForm
                  onSubmit={handleCreateUser}
                  onCancel={() => setShowCreateUserModal(false)}
                  isLoading={createUserMutation.isLoading}
                  mode="create"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 租户设置组件
const TenantSettings = ({ tenant, onEdit }) => {
  if (!tenant) {
    return (
      <div className="text-center py-12">
        <i className="fa fa-building text-gray-300 text-4xl mb-3"></i>
        <p className="text-gray-500">租户信息加载失败</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">租户信息</h2>
        <button
          onClick={onEdit}
          className="btn-primary"
        >
          <i className="fa fa-edit mr-2"></i>
          编辑信息
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-500">公司名称</label>
            <p className="mt-1 text-gray-900">{tenant.name}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">租户编码</label>
            <p className="mt-1 text-gray-900 font-mono">{tenant.code}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">公司地址</label>
            <p className="mt-1 text-gray-900">{tenant.address || '未设置'}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-500">联系人</label>
            <p className="mt-1 text-gray-900">{tenant.contact_name || '未设置'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">联系电话</label>
            <p className="mt-1 text-gray-900">{tenant.contact_phone || '未设置'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">联系邮箱</label>
            <p className="mt-1 text-gray-900">{tenant.contact_email || '未设置'}</p>
          </div>
        </div>
      </div>

      {tenant.description && (
        <div>
          <label className="text-sm font-medium text-gray-500">公司简介</label>
          <p className="mt-1 text-gray-900 whitespace-pre-line">{tenant.description}</p>
        </div>
      )}

      <div className="pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          <p>创建时间: {new Date(tenant.created_at).toLocaleString('zh-CN')}</p>
          <p className="mt-1">最后更新: {new Date(tenant.updated_at).toLocaleString('zh-CN')}</p>
        </div>
      </div>
    </div>
  );
};

// 用户管理组件
const UserManagement = ({ users, onEdit, onDelete, onCreate }) => {
  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: '管理员', color: 'badge-danger' },
      manager: { label: '经理', color: 'badge-warning' },
      user: { label: '用户', color: 'badge-primary' },
    };
    
    const config = roleConfig[role] || { label: role, color: 'badge-gray' };
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '从未登录';
    try {
      return new Date(dateString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '无效日期';
    }
  };

  // 生成头像URL
  const getAvatarUrl = (user) => {
    if (user.avatar_url) return user.avatar_url;
    const initials = user.full_name ? user.full_name.slice(0, 2) : user.username.slice(0, 2);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=random&color=fff&size=128`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">用户管理</h2>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            共 {users.length} 个用户
          </div>
          <button
            onClick={onCreate}
            className="btn-primary"
          >
            <i className="fa fa-plus mr-2"></i>
            添加用户
          </button>
        </div>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-12">
          <i className="fa fa-users text-gray-300 text-4xl mb-3"></i>
          <p className="text-gray-500">暂无用户</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    用户信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    部门/职位
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    角色
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    最后登录
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img
                          className="h-10 w-10 rounded-full"
                          src={getAvatarUrl(user)}
                          alt={user.full_name || user.username}
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name || user.username}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{user.department || '未设置'}</div>
                      <div className="text-sm text-gray-500">{user.position || '未设置'}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(user.last_login)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => onEdit(user)}
                        className="text-primary hover:text-blue-700 mr-3"
                      >
                        <i className="fa fa-edit mr-1"></i>编辑
                      </button>
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => onDelete(user.id)}
                          className="text-danger hover:text-red-700"
                        >
                          <i className="fa fa-trash mr-1"></i>禁用
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// 角色权限组件
const RolePermissions = () => {
  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: '管理员', color: 'badge-danger' },
      manager: { label: '经理', color: 'badge-warning' },
      user: { label: '用户', color: 'badge-primary' },
    };
    
    const config = roleConfig[role] || { label: role, color: 'badge-gray' };
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const roles = [
    {
      name: '管理员',
      value: 'admin',
      permissions: [
        '管理所有租户设置',
        '管理所有用户',
        '创建、编辑、删除所有数据',
        '查看所有系统日志',
        '配置系统参数'
      ]
    },
    {
      name: '经理',
      value: 'manager',
      permissions: [
        '管理部门用户',
        '创建、编辑、删除部门数据',
        '审批巡检任务',
        '查看部门报表',
        '管理培训计划'
      ]
    },
    {
      name: '用户',
      value: 'user',
      permissions: [
        '查看个人任务',
        '提交巡检结果',
        '参加培训',
        '查看个人证件',
        '查看通知'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium text-gray-900">角色权限说明</h2>
      <p className="text-gray-600">系统内置三种角色，每个角色拥有不同的权限级别。</p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {roles.map((role) => (
          <div key={role.value} className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
              {getRoleBadge(role.value)}
            </div>
            <ul className="space-y-2">
              {role.permissions.map((permission, index) => (
                <li key={index} className="flex items-start">
                  <i className="fa fa-check text-green-500 mt-1 mr-2"></i>
                  <span className="text-gray-600">{permission}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

// 系统日志组件
const SystemLogs = () => {
  const [filters, setFilters] = useState({
    type: '',
    date_from: '',
    date_to: '',
    page: 1
  });

  const { data: logsData, isLoading } = useQuery(
    ['systemLogs', filters],
    () => logAPI.getLogs(filters),
    {
      keepPreviousData: true
    }
  );

  const { data: statsData } = useQuery(
    'logStatistics',
    () => logAPI.getStatistics()
  );

  const exportMutation = useMutation(
    () => logAPI.exportLogs(filters),
    {
      onSuccess: (response) => {
        const { export: exportData } = response.data;
        const blob = new Blob([exportData.content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = exportData.filename;
        link.click();
        toast.success('日志导出成功');
      },
      onError: () => {
        toast.error('导出失败');
      }
    }
  );

  const logs = logsData?.data?.logs || [];
  const pagination = logsData?.data?.pagination || {};
  const stats = statsData?.data?.statistics || {};

  const getLogTypeBadge = (type) => {
    const typeConfig = {
      inspection: { label: '巡检', color: 'bg-blue-100 text-blue-800' },
      training: { label: '培训', color: 'bg-green-100 text-green-800' },
      certificate: { label: '证件', color: 'bg-yellow-100 text-yellow-800' },
      system: { label: '系统', color: 'bg-gray-100 text-gray-800' },
      user: { label: '用户', color: 'bg-purple-100 text-purple-800' }
    };
    const config = typeConfig[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">系统日志</h2>
          <p className="text-gray-600 text-sm mt-1">系统操作日志记录，包含用户操作、系统事件等信息。</p>
        </div>
        <button
          onClick={() => exportMutation.mutate()}
          className="btn-secondary"
          disabled={exportMutation.isLoading}
        >
          <i className="fa fa-download mr-2"></i>
          {exportMutation.isLoading ? '导出中...' : '导出日志'}
        </button>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500">今日日志</div>
            <div className="text-2xl font-bold text-gray-900">{stats.today_logs || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500">本周日志</div>
            <div className="text-2xl font-bold text-gray-900">{stats.week_logs || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500">本月日志</div>
            <div className="text-2xl font-bold text-gray-900">{stats.month_logs || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-500">总日志数</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_logs || 0}</div>
          </div>
        </div>
      )}

      {/* 过滤器 */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">日志类型</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
              className="form-input"
            >
              <option value="">全部类型</option>
              <option value="inspection">巡检</option>
              <option value="training">培训</option>
              <option value="certificate">证件</option>
              <option value="system">系统</option>
              <option value="user">用户</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">开始日期</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value, page: 1 })}
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">结束日期</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value, page: 1 })}
              className="form-input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ type: '', date_from: '', date_to: '', page: 1 })}
              className="btn-secondary w-full"
            >
              <i className="fa fa-refresh mr-2"></i>
              重置筛选
            </button>
          </div>
        </div>
      </div>

      {/* 日志列表 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="spinner mx-auto"></div>
              <p className="mt-4 text-gray-600">加载日志中...</p>
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <i className="fa fa-history text-gray-300 text-4xl mb-3"></i>
            <p className="text-gray-500">暂无日志记录</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">时间</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(log.created_at).toLocaleString('zh-CN')}
                        <div className="text-xs text-gray-400">{log.time_ago}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getLogTypeBadge(log.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.action}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">
                        {log.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {log.user?.full_name || log.user?.username || `用户${log.user?.id}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  共 {pagination.total} 条记录，第 {pagination.page}/{pagination.pages} 页
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                    disabled={!pagination.has_prev}
                    className="btn-secondary disabled:opacity-50"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={!pagination.has_next}
                    className="btn-secondary disabled:opacity-50"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// 租户表单组件
const TenantForm = ({ tenant, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      name: tenant.name,
      description: tenant.description || '',
      logo_url: tenant.logo_url || '',
      address: tenant.address || '',
      contact_name: tenant.contact_name || '',
      contact_phone: tenant.contact_phone || '',
      contact_email: tenant.contact_email || '',
    }
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="form-label">公司名称 *</label>
          <input
            type="text"
            {...register('name', { required: '请输入公司名称' })}
            className="form-input"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-danger">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="form-label">公司地址</label>
          <input
            type="text"
            {...register('address')}
            className="form-input"
            placeholder="请输入公司地址"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">联系人 *</label>
            <input
              type="text"
              {...register('contact_name', { required: '请输入联系人' })}
              className="form-input"
            />
            {errors.contact_name && (
              <p className="mt-1 text-sm text-danger">{errors.contact_name.message}</p>
            )}
          </div>

          <div>
            <label className="form-label">联系电话</label>
            <input
              type="text"
              {...register('contact_phone')}
              className="form-input"
              placeholder="请输入联系电话"
            />
          </div>
        </div>

        <div>
          <label className="form-label">联系邮箱 *</label>
          <input
            type="email"
            {...register('contact_email', { 
              required: '请输入联系邮箱',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: '请输入有效的邮箱地址'
              }
            })}
            className="form-input"
          />
          {errors.contact_email && (
            <p className="mt-1 text-sm text-danger">{errors.contact_email.message}</p>
          )}
        </div>

        <div>
          <label className="form-label">Logo URL</label>
          <input
            type="url"
            {...register('logo_url')}
            className="form-input"
            placeholder="请输入Logo图片地址"
          />
        </div>

        <div>
          <label className="form-label">公司简介</label>
          <textarea
            {...register('description')}
            rows="3"
            className="form-input"
            placeholder="请输入公司简介"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
        >
          取消
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              保存中...
            </>
          ) : (
            '保存更改'
          )}
        </button>
      </div>
    </form>
  );
};

// 用户表单组件
const UserForm = ({ user, onSubmit, onCancel, isLoading, mode = 'edit' }) => {
  const isCreateMode = mode === 'create';
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      username: user?.username || '',
      full_name: user?.full_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      role: user?.role || 'user',
      department: user?.department || '',
      position: user?.position || '',
      avatar_url: user?.avatar_url || '',
      is_active: user?.is_active ?? true,
      password: '',
    }
  });

  const fullName = watch('full_name');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {isCreateMode && (
          <div>
            <label className="form-label">用户名 *</label>
            <input
              type="text"
              {...register('username', { required: '请输入用户名' })}
              className="form-input"
              placeholder="请输入用户名（用于登录）"
            />
            {errors.username && (
              <p className="mt-1 text-sm text-danger">{errors.username.message}</p>
            )}
          </div>
        )}

        <div>
          <label className="form-label">姓名</label>
          <input
            type="text"
            {...register('full_name')}
            className="form-input"
            placeholder="请输入用户姓名"
          />
        </div>

        <div>
          <label className="form-label">邮箱 *</label>
          <input
            type="email"
            {...register('email', { 
              required: '请输入邮箱地址',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: '请输入有效的邮箱地址'
              }
            })}
            className="form-input"
            placeholder="请输入邮箱地址"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-danger">{errors.email.message}</p>
          )}
        </div>

        {isCreateMode && (
          <div>
            <label className="form-label">密码 *</label>
            <input
              type="password"
              {...register('password', { 
                required: '请输入密码',
                minLength: {
                  value: 6,
                  message: '密码至少6位'
                }
              })}
              className="form-input"
              placeholder="请输入初始密码"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-danger">{errors.password.message}</p>
            )}
          </div>
        )}

        <div>
          <label className="form-label">联系电话</label>
          <input
            type="text"
            {...register('phone')}
            className="form-input"
            placeholder="请输入联系电话"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">角色 *</label>
            <select
              {...register('role', { required: '请选择角色' })}
              className="form-input"
            >
              <option value="user">普通用户</option>
              <option value="manager">经理</option>
              <option value="admin">管理员</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-danger">{errors.role.message}</p>
            )}
          </div>

          {!isCreateMode && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                {...register('is_active')}
                className="h-4 w-4 text-primary border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                账户启用
              </label>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">部门</label>
            <input
              type="text"
              {...register('department')}
              className="form-input"
              placeholder="请输入部门"
            />
          </div>

          <div>
            <label className="form-label">职位</label>
            <input
              type="text"
              {...register('position')}
              className="form-input"
              placeholder="请输入职位"
            />
          </div>
        </div>

        <div>
          <label className="form-label">头像URL</label>
          <input
            type="url"
            {...register('avatar_url')}
            className="form-input"
            placeholder="留空将自动生成头像"
          />
          <p className="mt-1 text-xs text-gray-500">
            留空将根据姓名自动生成头像
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
        >
          取消
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              保存中...
            </>
          ) : (
            isCreateMode ? '创建用户' : '保存更改'
          )}
        </button>
      </div>
    </form>
  );
};

export default SystemPage;